import { v4 as uuid } from 'uuid';
import type { DescriptionStratum } from '@ast-data/shared';

/** A stratum row in the editor — a DescriptionStratum plus a stable local key. */
export type StratumRow = DescriptionStratum & { id: string };

/** Wrap stored strata as editor rows (on open / reset). */
export function toStratumRows(
    strata: readonly DescriptionStratum[] | undefined
): StratumRow[] {
    return (strata ?? []).map((entry) => ({ id: uuid(), ...entry }));
}

/**
 * Strip the local key + empty rows, leaving the storable list.
 *
 * The seeding's bookkeeping (`sourceId`, `seededName`) travels with the row and
 * is written only where there is something to write — see DescriptionStratum.
 */
export function cleanStrata(rows: readonly StratumRow[]): DescriptionStratum[] {
    return rows
        .map((row) => ({
            name: row.name.trim(),
            length: row.length.trim(),
            width: row.width.trim(),
            depth: row.depth.trim(),
            ...(row.diameter?.trim() ? { diameter: row.diameter.trim() } : {}),
            ...(row.sourceId ? { sourceId: row.sourceId } : {}),
            ...(row.seededName ? { seededName: row.seededName } : {})
        }))
        .filter(
            (entry) =>
                entry.name !== '' ||
                entry.length !== '' ||
                entry.width !== '' ||
                entry.depth !== '' ||
                Boolean(entry.diameter)
        );
}

/** Stable, order-sensitive key for the list (dirty comparison). */
export function strataKey(rows: readonly DescriptionStratum[]): string {
    return JSON.stringify(
        rows.map((row) => [
            row.name.trim(),
            row.length.trim(),
            row.width.trim(),
            row.depth.trim(),
            (row.diameter ?? '').trim()
        ])
    );
}
